import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Search } from 'lucide-react'
import usePopover, { panelStyle } from './usePopover'
import { kit as K } from './strings'
import './Select.css'

/**
 * The app's dropdown — a listbox, not a native `<select>`.
 *
 * A native select paints its own popup from the OS: system font, system row
 * height, system highlight, and on Windows a hard blue bar that belongs to no
 * design system. It also cannot show a check against the chosen row, cannot be
 * styled per option, and on a long list gives no way to type toward a value.
 * The field shell was ours and the list was not, so every filter row in the app
 * changed character the moment it was opened.
 *
 * This keeps the same props, so all thirteen call sites are unchanged, and adds
 * the keyboard contract a listbox owes: ↑/↓ to move, Home/End to jump, Enter or
 * Space to take, Escape to leave, and type-ahead that jumps to the first option
 * starting with what you typed.
 *
 * The panel is portalled and anchored in script (usePopover) because these open
 * inside drawers and table toolbars that clip.
 *
 * A LIST LONG ENOUGH TO SCROLL CARRIES A SEARCH FIELD. Departments and groups are
 * the reason: an installation has hundreds, and finding one by dragging a scrollbar
 * past the rest — or by trusting a type-ahead that matches the START of a label
 * nobody remembers exactly — is not finding it. The field appears on its own above
 * `SEARCH_FROM` options and matches ANYWHERE in the label, the way `GroupPicker`
 * does for the multi-select beside it.
 *
 * @param {{value: string|number, label: string}[]} options
 * @param {string} [placeholder]  the empty row, kept selectable so a filter can
 *   be cleared — the legacy select2's `data-allow-clear`.
 * @param {boolean} [searchable]  forces the field on or off; by default the list
 *   decides for itself.
 */

/** Below this many options the eye is faster than a filter. */
const SEARCH_FROM = 8
export default function Select({
  id,
  value,
  onChange,
  /* Defaulted: a caller that forgets this used to throw while rendering — `[...options]`
     on undefined — and take the whole page down with it. An empty select is a visible,
     recoverable mistake; a blank screen is not. */
  options = [],
  placeholder,
  disabled = false,
  /* A required select that fails validation has to LOOK failed, the way
     TextField does. Named rather than swallowed by ...rest, which used to
     forward it to the DOM and warn on every render. */
  invalid = false,
  searchable,
  className = '',
  ...rest
}) {
  const { open, setOpen, close, pos, rootRef, panelRef, id: panelId } = usePopover({
    align: 'start',
    matchWidth: true,
  })

  const [query, setQuery] = useState('')
  const searchRef = useRef(null)
  const withSearch = searchable ?? options.length >= SEARCH_FROM

  /* The empty row is an option like any other, so one list drives the keyboard,
     the rendering and the lookup. It is dropped while a search is up: "clear the
     choice" is not one of the results of typing a name. */
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const matches = needle
      ? options.filter((option) => String(option.label).toLowerCase().includes(needle))
      : options
    return placeholder != null && !needle
      ? [{ value: '', label: placeholder }, ...matches]
      : matches
  }, [options, placeholder, query])

  const current = value ?? ''
  const selectedIndex = rows.findIndex((row) => String(row.value) === String(current))
  const [activeIndex, setActiveIndex] = useState(selectedIndex)
  const listRef = useRef(null)
  const typed = useRef({ text: '', at: 0 })

  // Opening lands on the current value, wherever the highlight was left before.
  useEffect(() => {
    if (open) setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0)
  }, [open, selectedIndex])

  /* A dropdown never opens onto the last search, and the caret starts in the field
     so the first keystroke is already the search. */
  useEffect(() => {
    if (open) searchRef.current?.focus()
    else setQuery('')
  }, [open])

  // Typing narrows the list under the highlight, which then has to come back to the top.
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!open) return
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [open, activeIndex])

  const take = (index) => {
    const row = rows[index]
    if (!row) return
    onChange?.(row.value === '' ? '' : String(row.value))
    close()
    rootRef.current?.querySelector('button')?.focus()
  }

  const onKeyDown = (event) => {
    if (disabled) return

    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault()
        setOpen(true)
      }
      return
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setActiveIndex((index) => Math.min(index + 1, rows.length - 1))
        return
      case 'ArrowUp':
        event.preventDefault()
        setActiveIndex((index) => Math.max(index - 1, 0))
        return
      case 'Home':
        event.preventDefault()
        setActiveIndex(0)
        return
      case 'End':
        event.preventDefault()
        setActiveIndex(rows.length - 1)
        return
      case 'Enter':
        event.preventDefault()
        take(activeIndex)
        return
      case ' ':
        /* Space takes the highlighted row — except while typing in the search
           field, where a space is a space and "أمن المعلومات" is two words. */
        if (withSearch) return
        event.preventDefault()
        take(activeIndex)
        return
      case 'Tab':
      case 'Escape':
        close()
        rootRef.current?.querySelector('button')?.focus()
        return
      default:
        break
    }

    // Type-ahead: successive letters within a second build one search string. Only
    // where there is no search field — with one, the letters belong in it.
    if (!withSearch && event.key.length === 1) {
      const now = event.timeStamp
      typed.current.text = now - typed.current.at > 900 ? event.key : typed.current.text + event.key
      typed.current.at = now
      const needle = typed.current.text.toLowerCase()
      const found = rows.findIndex((row) => String(row.label).toLowerCase().startsWith(needle))
      if (found >= 0) setActiveIndex(found)
    }
  }

  const selected = selectedIndex >= 0 ? rows[selectedIndex] : null
  const showsPlaceholder = !selected || selected.value === ''

  return (
    <div className={`select ${className}`.trim()} ref={rootRef}>
      <button
        id={id}
        type="button"
        className={`select__control${open ? ' is-open' : ''}${
          showsPlaceholder ? ' is-empty' : ''
        }${invalid ? ' is-invalid' : ''}`}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => setOpen((value_) => !value_)}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        {...rest}
      >
        <span className="select__value">
          <bdi>{selected ? selected.label : (placeholder ?? '')}</bdi>
        </span>
        <ChevronDown className="select__caret" size={16} strokeWidth={2} aria-hidden="true" />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            className={`select__panel select__panel--${pos?.placement ?? 'below'}`}
            style={panelStyle(pos)}
          >
            {withSearch && (
              <div className="select__search">
                <Search className="select__search-icon" size={15} strokeWidth={1.9} aria-hidden="true" />
                <input
                  ref={searchRef}
                  type="text"
                  className="select__search-field"
                  value={query}
                  placeholder={K.selectSearch}
                  aria-label={K.selectSearch}
                  aria-controls={`${panelId}-list`}
                  aria-activedescendant={`${panelId}-${activeIndex}`}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={onKeyDown}
                />
              </div>
            )}

            {rows.length === 0 && <p className="select__empty">{K.selectNoMatches}</p>}

            <ul
              className="select__list"
              id={`${panelId}-list`}
              role="listbox"
              ref={listRef}
              aria-activedescendant={`${panelId}-${activeIndex}`}
            >
              {rows.map((row, index) => {
                const isSelected = String(row.value) === String(current)
                return (
                  <li
                    key={`${row.value}`}
                    id={`${panelId}-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    className={`select__option${index === activeIndex ? ' is-active' : ''}${
                      isSelected ? ' is-selected' : ''
                    }${row.value === '' ? ' select__option--empty' : ''}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => take(index)}
                  >
                    <bdi className="select__option-label">{row.label}</bdi>
                    {isSelected && (
                      <Check className="select__tick" size={15} strokeWidth={2.5} aria-hidden="true" />
                    )}
                  </li>
                )
              })}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  )
}
